import { useState, type FormEvent } from 'react';
import { confirmPasswordReset, requestPasswordReset, signIn, type SignInResult } from '../auth';

type Props = { onSignedIn: () => void };

/** signIn: 通常ログイン / sendCode: 再設定コードの送信 / reset: コードで再設定 */
type Mode = 'signIn' | 'sendCode' | 'reset';

const SUBMIT_LABEL: Record<Mode, string> = {
  signIn: 'ログイン',
  sendCode: '確認コードを送る',
  reset: 'パスワードを再設定',
};

export function Login({ onSignedIn }: Props) {
  const [mode, setMode] = useState<Mode>('signIn');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [challenge, setChallenge] = useState<Extract<SignInResult, { status: 'newPasswordRequired' }> | null>(
    null,
  );
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const go = (next: Mode) => {
    setMode(next);
    setError(null);
    setNotice(null);
  };

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      if (challenge) {
        await challenge.completeWith(newPassword);
      } else if (mode === 'sendCode') {
        await requestPasswordReset(email.trim());
        go('reset');
        setNotice('確認コードをメールで送りました (届くまで数分かかることがあります)');
        setBusy(false);
        return;
      } else {
        // 再設定の直後はそのままログインまで進める
        if (mode === 'reset') {
          await confirmPasswordReset(email.trim(), code, newPassword);
        }
        const result = await signIn(email.trim(), mode === 'reset' ? newPassword : password);
        if (result.status === 'newPasswordRequired') {
          setChallenge(result);
          setBusy(false);
          return;
        }
      }
      onSignedIn();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'ログインに失敗しました');
      setBusy(false);
    }
  };

  return (
    <main className="gate">
      <div className="gate__card">
        <div className="gate__brand">
          <div className="gate__mark" aria-hidden>
            鍛
          </div>
          <div>
            <div className="gate__name">FORGE</div>
            <div className="gate__tag">筋トレの記録を、鍛えた分だけ残す</div>
          </div>
        </div>

        <form className="gate__form" onSubmit={submit}>
          {challenge ? (
            <>
              <p className="hint" style={{ marginTop: 0 }}>
                初回ログインです。新しいパスワードを設定してください (12文字以上・英小文字と数字を含む)。
              </p>
              <input
                className="field"
                type="password"
                value={newPassword}
                placeholder="新しいパスワード"
                autoComplete="new-password"
                required
                minLength={12}
                onChange={(e) => setNewPassword(e.target.value)}
              />
            </>
          ) : (
            <>
              {mode === 'sendCode' && (
                <p className="hint" style={{ marginTop: 0 }}>
                  登録したメールアドレスに確認コードを送ります。
                </p>
              )}
              {mode !== 'reset' && (
                <input
                  className="field"
                  type="email"
                  value={email}
                  placeholder="メールアドレス"
                  autoComplete="username"
                  required
                  onChange={(e) => setEmail(e.target.value)}
                />
              )}
              {mode === 'signIn' && (
                <input
                  className="field"
                  type="password"
                  value={password}
                  placeholder="パスワード"
                  autoComplete="current-password"
                  required
                  onChange={(e) => setPassword(e.target.value)}
                />
              )}
              {mode === 'reset' && (
                <>
                  <input
                    className="field"
                    type="text"
                    value={code}
                    placeholder="確認コード"
                    autoComplete="one-time-code"
                    inputMode="numeric"
                    required
                    onChange={(e) => setCode(e.target.value)}
                  />
                  <input
                    className="field"
                    type="password"
                    value={newPassword}
                    placeholder="新しいパスワード"
                    autoComplete="new-password"
                    required
                    minLength={12}
                    onChange={(e) => setNewPassword(e.target.value)}
                  />
                  <p className="hint">12文字以上・英小文字と数字を含む</p>
                </>
              )}
            </>
          )}

          {notice && !error && <div className="hint">{notice}</div>}
          {error && <div className="alert">{error}</div>}

          <button className="btn btn--primary btn--wide" type="submit" disabled={busy}>
            {busy ? <span className="spinner" /> : challenge ? 'パスワードを設定' : SUBMIT_LABEL[mode]}
          </button>

          {!challenge && (
            <button className="btn btn--wide" type="button" onClick={() => go(mode === 'signIn' ? 'sendCode' : 'signIn')}>
              {mode === 'signIn' ? 'パスワードを忘れた' : 'ログインに戻る'}
            </button>
          )}
        </form>
      </div>
    </main>
  );
}
